import React, { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import SEO, { seoConfigs, generateOrganizationSchema, generateWebApplicationSchema } from "../components/SEO";
import { ArrowRight, Shield, Clock, Calculator, Leaf } from "lucide-react";
import { isFeatureEnabled } from "../config/featureFlags";
import { statsSnapshot, fmtInt, fmtMEur, fmtMilesAbajoRaw } from "../lib/contratacionStats";

// Sprint 119.109 — Lazy PoolFinderCTA (arrossega maplibre-gl ~500KB).
// PageSpeed mobile: "JS no utilitzat 499 KiB". PoolFinderCTA no és visible
// al primer viewport (està a la 2a secció sota el hero) — perfecte candidat
// per lazy load. Chrome descarrega el chunk quan React renderitza aquesta
// secció, no bloqueja LCP del hero.
const PoolFinderCTA = lazy(() => import("../components/PoolFinderCTA"));

// Fallback UI mentre el chunk PoolFinderCTA carrega
const PoolFinderCTAFallback = () => (
  <div className="min-h-[400px] flex items-center justify-center border border-slate-200 rounded-lg bg-slate-50">
    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format time ago
function timeAgo(dateString, t) {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t("activity.justNow");
  if (diffMins < 60) return t("activity.minutesAgo", { count: diffMins });
  if (diffHours < 24) return t("activity.hoursAgo", { count: diffHours });
  if (diffDays < 7) return t("activity.daysAgo", { count: diffDays });
  return date.toLocaleDateString();
}

// Per sota d'aquesta xifra, la tira de prova social no es pinta.
const MIN_PROVA_SOCIAL = 250;

const LandingPageV2 = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activity, setActivity] = useState(null);
  const lang = i18n.language || 'es';
  const seo = seoConfigs.home[lang] || seoConfigs.home.es;

  useEffect(() => {
    axios.get(`${API}/stats/activity`)
      .then(res => setActivity(res.data))
      .catch(err => console.log("Activity fetch error:", err));
  }, []);

  // Combined structured data for homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      generateOrganizationSchema(),
      generateWebApplicationSchema(),
      {
        "@type": "WebPage",
        "@id": "https://poollex.io/",
        "url": "https://poollex.io/",
        "name": seo.title,
        "description": seo.description,
        "isPartOf": { "@id": "https://poollex.io/#website" },
        "inLanguage": lang
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SEO 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path="/"
        structuredData={structuredData}
      />
      <SiteHeader />

      {/* Sprint 119.107 a11y — <main> landmark envolta contingut principal
          entre SiteHeader i SiteFooter. Lighthouse: "El documento no tiene
          un punto de referencia principal". */}
      <main id="main-content">

      {/* BLOQUE 1 - HERO PRINCIPAL. Sprint 116 — grid 2 cols a lg: text
          esquerra + imatge mostra Ficha Tècnica dreta. A ≤md tot es
          col·lapsa a una columna (imatge a sota del block CTA).
          Sprint 119.50 — Fons fotogràfic (landing-hero.jpg): blueprint
          piscina + icones sostenibilitat + onades. Substitueix el pattern
          de graella (redundant amb el blueprint de la nova imatge). */}
      <section className="relative pt-16 md:pt-20 pb-10 md:pb-12 px-4 overflow-hidden" data-testid="hero-section">
        {/* Sprint 119.102 — LCP optimization: fetchpriority="high" força
            que Chrome descarregui la hero image amb prioritat màxima,
            millorant el Largest Contentful Paint (mètrica Core Web Vital). */}
        <img
          src="/img/landing-hero.jpg"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay blanc suau a mobile per legibilitat (a desktop l'àrea esquerra ja és clara) */}
        <div className="absolute inset-0 bg-white/50 lg:bg-transparent" />
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Columna esquerra — copy + CTA */}
            <div className="text-center lg:text-left">
              {/* Sprint 147 — El logotip baixa DINS la columna i perd el fons
                  blanc (poollex-logo-hero.png, calat des del PNG de paleta
                  que era opac). Centrat i a h-16 duplicava el de la capçalera
                  i empenyia l'H1 fora de la primera pantalla, que és on es
                  decideix si algú crea la Ficha. */}
              <img
                src="/poollex-logo-hero.png"
                alt="PoolLex.io"
                width="910"
                height="190"
                className="h-9 md:h-10 w-auto mb-6 mx-auto lg:mx-0"
                data-testid="hero-logo"
              />

              {/* Sprint 147 — La línia verda puja aquí; era al peu del hero,
                  després de la imatge. Substitueix el hook blau, que deia
                  gairebé el mateix ("Certificación digital de sostenibilidad")
                  i competia amb l'H1 sent més gran que ell. La norma es manté
                  al subtítol. */}
              {/* text-xl a mòbil: a text-2xl ocupava tres línies i, sumada a
                  l'H1, deixava el botó fora de la primera pantalla. */}
              <p
                className="[font-family:'Outfit',ui-sans-serif,system-ui,sans-serif] font-bold text-emerald-700 text-xl md:text-3xl leading-tight tracking-tight mb-4 md:mb-5"
                data-testid="hero-promise"
              >
                {t("home.hero_promise_prefix")} {t("home.hero_promise_suffix")}
              </p>

              {/* Sprint 147 — L'H1 deia "La certificación de sostenibilidad
                  para tu piscina" mentre el <title> i les keywords indexades
                  parlen de cost i consum ("cuanto gasta una piscina al mes").
                  Qui clicava des d'aquelles consultes aterrava en una altra
                  cosa. Ara diuen el mateix: consum/cost primer, norma segon. */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 mb-6 leading-tight" data-testid="hero-title">
                {t("home.hero_title")}
              </h1>

              <p className="text-lg text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0" data-testid="hero-subtitle">
                {t("home.hero_subtitle")}
              </p>

              {/* Sprint 147 — Un sol CTA. El segon ("Comprobar mi piscina")
                  enviava a /pool-check, que és una altra cosa, i repartia el
                  clic just al punt on la pàgina ha de demanar UNA acció. La
                  ruta segueix accessible des del menú i des de §00. */}
              <div className="flex justify-center lg:justify-start">
                <button
                  onClick={() => navigate("/ficha-tecnica")}
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-none px-8 py-4 font-medium transition-all text-sm tracking-wide inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta"
                >
                  {t("home.cta_passport")}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Sprint 118.5 — Micro-badge urgència sota els CTAs.
                  Només visible si hi ha activitat real avui. */}
              {activity?.pools_today > 0 && (
                <p className="mt-4 text-xs text-slate-500 inline-flex items-center gap-1.5 justify-center lg:justify-start w-full lg:w-auto" data-testid="hero-urgency">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
                  <span className="font-mono text-emerald-700 font-semibold">+{activity.pools_today}</span>
                  <span>{t("activity.fichasToday", "Fichas generadas hoy")}</span>
                </p>
              )}
            </div>

            {/* Columna dreta — captura de la Ficha Técnica PoolLex ID
                (Sprint 118.7: el badge EN 17645 ja va dins la pròpia
                imatge, no cal overlay separat).
                Sprint 119.54 — Sello EN 17645 (Clase A) superposat a la
                cantonada inferior-esquerra de la Ficha com a "aprovat".
                PNG amb fons transparent, colors teal+navy → harmonitza
                amb el fons blau del hero. */}
            <div className="relative mt-4 lg:mt-0 flex flex-col items-center lg:items-end" data-testid="hero-image">
              <div className="relative">
                <img
                  src="/web-plx-0000001.png"
                  alt="Ficha Técnica digital de piscina — PLX-0000001"
                  className="w-auto h-auto max-h-[380px] object-contain shadow-xl border border-slate-200 bg-white"
                  loading="eager"
                />
                {/* V4 — El segell EN 17645 se'n va al §01, al costat de
                    la targeta que explica que la classificació va dins del
                    document. Al hero era la norma fent de reclam, que és
                    justament el que hem deixat de fer. */}
              </div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-6">
                Ficha Técnica PoolLex ID
              </p>
            </div>
          </div>

          {/* Sprint 147 — Aquí hi havia la promesa a 6xl i el tagline. La
              promesa ha pujat dins la columna esquerra: a 6xl i just sota
              l'H1 eren DUES capçaleres de la mateixa mida dient gairebé el
              mateix, i la de baix cridava més que l'H1 de veritat.
              El tagline se'n va perquè el subtítol nou ja diu la norma. */}
          <p className="mt-12 pt-6 border-t border-slate-200 text-center text-sm text-slate-500 leading-relaxed max-w-2xl mx-auto" data-testid="hero-tagline">
            {t("tagline")}
          </p>
        </div>
      </section>

      {/* BLOQUE §01 - EL DOCUMENTO (landing V4, 26/10/2026).
          Tres mesos amb la certificacio voluntaria EN 17645 al hero i la
          conclusio contra el pla de negoci es que si no es obligatori la
          gent no se la treu. La norma no desapareix: deixa de ser la
          porta i passa a ser una sortida mes del document.
          docs/specs/landing-v4.md */}
      <section className="pt-12 md:pt-16 pb-16 md:pb-20 px-4 bg-white" data-testid="documento-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-6">
            {t("landing_v4.doc_label")}
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6 leading-tight" data-testid="documento-title">
            {t("landing_v4.doc_title")}
          </h2>
          <p className="max-w-3xl text-lg text-slate-700 leading-relaxed mb-12">
            {t("landing_v4.doc_intro")}
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-blue-700 p-7 flex flex-col gap-3" data-testid="doc-trazable">
              <h3 className="font-semibold text-slate-900 text-lg leading-snug">
                {t("landing_v4.doc_c1_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("landing_v4.doc_c1_body")}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-emerald-600 p-7 flex flex-col gap-3" data-testid="doc-en17645">
              <img
                src="/img/en17645-seal.png"
                alt="Sello Piscina Sostenible EN 17645"
                className="w-20 h-auto mb-1"
                loading="lazy"
              />
              <h3 className="font-semibold text-slate-900 text-lg leading-snug">
                {t("landing_v4.doc_c2_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("landing_v4.doc_c2_body")}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-slate-700 p-7 flex flex-col gap-3" data-testid="doc-presentar">
              <h3 className="font-semibold text-slate-900 text-lg leading-snug">
                {t("landing_v4.doc_c3_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("landing_v4.doc_c3_body")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOQUE §02 - QUIEN LO FIRMA (landing V4). El criteri col·legiat es
          el segon pilar del North Star i no sortia enlloc de la portada.
          Aqui va tambe el posicionament d'IA verificable: cada xifra es
          pot seguir fins a la font. */}
      <section className="py-16 md:py-20 px-4 bg-slate-50 border-t border-slate-100" data-testid="firma-section">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-6">
            {t("landing_v4.sign_label")}
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6 leading-tight" data-testid="firma-title">
            {t("landing_v4.sign_title")}
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            {t("landing_v4.sign_body")}
          </p>
        </div>
      </section>

      {/* BLOQUE §03 - CUANDO TE LO VAN A PEDIR (V4: puja del mig de la
          pagina a just darrere del document. Son els tres moments en que
          la gent actua, i estaven tres pantalles per sota del reclam. */}
      <section className="pt-12 md:pt-16 pb-20 md:pb-28 px-4 bg-white" data-testid="who-section">
        <div className="max-w-5xl mx-auto">
          {/* V4 — Fora l'assaig «tu piscina, tu activo» que hi havia aqui:
              tres paragrafs sobre patrimoni abans d'arribar als tres
              moments en que la gent actua de debo. Ara el bloc va al gra. */}
          <div className="mb-14">
            <p className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-6">
              {t("landing_v4.triggers_label")}
            </p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6 leading-tight" data-testid="who-title">
              {t("landing_v4.triggers_title")}
            </h2>
            <p className="max-w-3xl text-lg text-slate-700 leading-relaxed">
              {t("landing_v4.triggers_intro")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Trigger 1 — Vender */}
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-blue-700 p-7 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-blue-700 hover:shadow-lg transition-all" data-testid="who-trigger-sell">
              <p className="text-xs font-mono uppercase tracking-widest text-blue-700">
                {t("landing_v3.t1_label")}
              </p>
              <h3 className="text-xl font-semibold text-slate-900 leading-tight">
                {t("landing_v3.t1_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {t("landing_v3.t1_body")}
              </p>
              <button
                onClick={() => navigate("/ficha-tecnica?intent=sell")}
                className="text-sm font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all pt-3 border-t border-slate-200"
              >
                {t("landing_v3.t1_cta")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Trigger 2 — Ayuntamiento */}
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-blue-700 p-7 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-blue-700 hover:shadow-lg transition-all" data-testid="who-trigger-legal">
              <p className="text-xs font-mono uppercase tracking-widest text-blue-700">
                {t("landing_v3.t2_label")}
              </p>
              <h3 className="text-xl font-semibold text-slate-900 leading-tight">
                {t("landing_v3.t2_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {t("landing_v3.t2_body")}
              </p>
              <button
                onClick={() => navigate("/ficha-tecnica?intent=verify_legal")}
                className="text-sm font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all pt-3 border-t border-slate-200"
              >
                {t("landing_v3.t2_cta")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Trigger 3 — Sostenibilidad / Eficiencia (era: Alquilar/compartir) */}
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-emerald-600 p-7 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-emerald-600 hover:shadow-lg transition-all" data-testid="who-trigger-sustainability">
              <p className="text-xs font-mono uppercase tracking-widest text-emerald-700">
                {t("landing_v3.t3_label")}
              </p>
              <h3 className="text-xl font-semibold text-slate-900 leading-tight">
                {t("landing_v3.t3_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {t("landing_v3.t3_body")}
              </p>
              <button
                onClick={() => navigate("/ficha-tecnica")}
                className="text-sm font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all pt-3 border-t border-slate-200"
              >
                {t("landing_v3.t3_cta")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Sprint 118.5 — Social proof numèric.
          Sprint 148 — Hi entra el nombre de països. Deixa de ser només un
          recompte de gent i passa a dir que la cosa ja travessa fronteres,
          que és el que sosté una norma europea. El tram de països només es
          pinta si n'hi ha més d'un: amb un de sol, dir-ho restaria.
          Sprint 165 — Puja del peu del CTA final a sota del hero, abans del
          mapa: el mateix text, però el veu tothom i no només qui arriba al
          final. Mateixes claus d'i18n, així que va als sis idiomes. */}
      {/* V4 — Llindar. «Unete a 60 propietarios» convenç qui ja ha
          comprat i desanima qui dubta: una xifra petita com a prova
          social treballa en contra. Torna quan ajudi. */}
      {activity?.pools_registered >= MIN_PROVA_SOCIAL && (
        <section className="py-5 px-4 bg-white border-t border-slate-100" data-testid="social-proof-strip">
          {/* Text en línia, no flex: amb cada tros com a element flexible, al
              mòbil la xifra queda penjada al final de la línia i la paraula
              que la segueix baixa a la següent («... en 3 / países ...»).
              L'espai dur enganxa cada número a la seva paraula i la resta
              embolcalla sola. */}
          <p className="max-w-3xl mx-auto text-sm text-slate-500 text-center leading-relaxed" data-testid="final-cta-social-proof">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse align-middle mr-2" aria-hidden />
            {t("activity.joinPrefix", "Únete a")}{' '}
            <span className="font-mono font-bold text-slate-900 text-base">{activity.pools_registered.toLocaleString(i18n.language)}</span>
            {activity?.countries > 1 ? (
              <>
                {' '}{t("activity.joinMid", "propietarios ya documentados en")}{' '}
                <span className="font-mono font-bold text-slate-900 text-base">{activity.countries}</span>
                {' '}{t("activity.joinCountries", { count: activity.countries })}
              </>
            ) : (
              <>{' '}{t("activity.joinSuffix", "propietarios ya documentados")}</>
            )}
          </p>
        </section>
      )}

      {/* Sprint 119.46/47 — Widget "Busca tu piscina" amb mapa EMBEGUT
          (no modal). Reutilitza PoolLocationPicker amb embedded={true}.
          Al confirmar navega a /ficha-tecnica amb address/cadastral_ref/
          city/region pre-omplerts. Mida 5xl per què el mapa + panell
          lateral (320px) hi càpiguin còmodament. */}
      <section className="py-12 md:py-16 px-4 bg-gradient-to-b from-white via-emerald-50/30 to-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto">
          {/* Sprint 119.109 — Suspense fallback amb spinner mentre carrega
              el chunk PoolFinderCTA (maplibre-gl ~500KB). */}
          <Suspense fallback={<PoolFinderCTAFallback />}>
            <PoolFinderCTA />
          </Suspense>
        </div>
      </section>

      {/* Sprint 119.61 — MINI-BLOC "Actividad reciente" mogut aquí (era abans
          del CTA final). Just sota del mapa aporta prova social contextual:
          l'usuari veu l'invite a cercar la seva piscina + immediatament
          "l'última generació va ser fa X" → efecte urgència positiva. */}
      {activity && activity.latest_passport_at && (() => {
        const diffH = (Date.now() - new Date(activity.latest_passport_at)) / 3_600_000;
        const isLive = diffH < 24;
        // V4 — Si no hi ha hagut activitat en 24 h, el rètol no es pinta.
        // Abans baixava a ambre i seguia dient «última Ficha: fa 11 dies»:
        // un indicador d'activitat en viu que ensenya silenci diu el
        // contrari del que pretén.
        if (!isLive) return null;
        const dotClass = isLive ? "bg-emerald-500 animate-pulse" : "bg-amber-400";
        const labelClass = isLive ? "text-emerald-700" : "text-amber-700";
        return (
          <section className="py-6 px-4 bg-white border-t border-slate-100" data-testid="last-passport-strip">
            <div className="max-w-5xl mx-auto flex items-center justify-center gap-6 flex-wrap text-sm">
              <span className="inline-flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${dotClass}`} aria-hidden />
                <span className={`font-medium ${labelClass}`}>{isLive ? t("activity.liveBadge", "Activo ahora") : t("activity.recentBadge", "Actividad reciente")}</span>
              </span>
              <span className="text-slate-500 inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-slate-400" />
                <span>{t("activity.latestPassport", "Última Ficha Técnica generada")}:</span>
                <span className="font-medium text-slate-800 font-mono">{timeAgo(activity.latest_passport_at, t)}</span>
              </span>
              {activity.pools_this_week > 0 && (
                <span className="text-slate-500 inline-flex items-center gap-1.5">
                  <span className="font-mono text-slate-900 font-bold">+{activity.pools_this_week}</span>
                  <span>{t("activity.weeklyCounter", "esta semana")}</span>
                </span>
              )}
            </div>
          </section>
        );
      })()}

      {/* BLOQUE §00 - LOS 3 NIVELES POOLLEX (Sprint 118.8 — bloc estructurant
          per respondre "què és PoolLex". Ficha gratuita · Sello firmado ·
          Licencias B2B. Sense preus visibles (feedback_precios_voluntarios):
          l'única tarifa mostrada és GRATIS al freemium; la resta són CTAs
          consultius. Reemplaça la mescla anterior de 6 focus paral·lels. */}
      <section className="py-16 md:py-20 px-4 bg-white border-t border-slate-100" data-testid="niveles-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">
              §00 · {t("landing_v3.s00_label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-4 leading-tight">
              {t("landing_v3.s00_title_prefix")} <em className="italic font-normal text-blue-700">{t("landing_v3.s00_title_emerald")}</em>.
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {t("landing_v3.s00_intro")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Nivel 1 · Ficha + estimació EN 17645 · GRATIS */}
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-blue-700 p-7 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-blue-700 hover:shadow-lg transition-all" data-testid="nivel-ficha">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-mono uppercase tracking-widest text-blue-700">
                  {t("landing_v3.n1_label")}
                </p>
                <span className="text-[10px] font-mono font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 uppercase tracking-widest">
                  {t("landing_v3.n1_gratis")}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 leading-tight">
                {t("landing_v3.n1_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {t("landing_v3.n1_body_prefix")}<strong className="text-slate-900">{t("landing_v3.n1_body_strong")}</strong>{t("landing_v3.n1_body_suffix")}
              </p>
              {/* Mostra del document que rep el client. Captura real de la
                  sortida del generador, no una recreacio: es la primera
                  pagina de la Ficha de PLX-0000001, la piscina de demo. */}
              <img
                src="/img/documentos/doc-ficha.jpg"
                alt={t("landing_v3.n1_doc_alt")}
                width="900"
                height="562"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto border border-slate-200 bg-white"
              />
              <button
                onClick={() => navigate("/ficha-tecnica")}
                className="text-sm font-medium text-blue-700 hover:text-blue-800 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all pt-3 border-t border-slate-200"
              >
                {t("landing_v3.n1_cta")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Nivel 2 · Sello Piscina Sostenible firmado */}
            <div className="bg-white border border-emerald-200 border-t-[3px] border-t-emerald-600 p-7 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-emerald-600 hover:shadow-lg transition-all relative" data-testid="nivel-sello">
              <span className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                {t("landing_v3.n2_pill")}
              </span>
              <div className="flex items-center gap-2 mb-1">
                <Leaf className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-mono uppercase tracking-widest text-emerald-700">
                  {t("landing_v3.n2_label")}
                </p>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 leading-tight">
                {t("landing_v3.n2_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {t("landing_v3.n2_body_prefix")}<strong className="text-slate-900">{t("landing_v3.n2_body_strong")}</strong>{t("landing_v3.n2_body_suffix")}
              </p>
              {/* L'etiqueta EN 17645 amb les rodes de classificacio per
                  component, que es el que distingeix aquest document a
                  simple vista. Es mostra SENSE signar, decisio de Victor
                  del 05/09: la signatura del colegiat va a sobre. */}
              <img
                src="/img/documentos/doc-sello.jpg"
                alt={t("landing_v3.n2_doc_alt")}
                width="900"
                height="562"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto border border-emerald-200 bg-white"
              />
              <button
                onClick={() => navigate("/solicitar-sello")}
                className="text-sm font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all pt-3 border-t border-emerald-100"
              >
                {t("landing_v3.n2_cta")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Nivel 3 · Herramienta comercial per instal·ladors */}
            <div className="bg-slate-50 border border-slate-200 border-t-[3px] border-t-slate-700 p-7 flex flex-col gap-3 hover:-translate-y-0.5 hover:border-slate-700 hover:shadow-lg transition-all" data-testid="nivel-instalador">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-700 mb-1">
                {t("landing_v3.n3_label")}
              </p>
              <h3 className="text-xl font-semibold text-slate-900 leading-tight">
                {t("landing_v3.n3_title")}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">
                {t("landing_v3.n3_body_prefix")}<strong className="text-slate-900">{t("landing_v3.n3_body_strong")}</strong>{t("landing_v3.n3_body_suffix")}
              </p>
              {/* Informe de millores amb la marca de l'instal-lador al peu,
                  que es el que ven aquest nivell. L'identificador va com a
                  PLX-XXXXXXX: es una mostra, no una piscina concreta. */}
              <img
                src="/img/documentos/doc-mejoras.jpg"
                alt={t("landing_v3.n3_doc_alt")}
                width="900"
                height="562"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto border border-slate-200 bg-white"
              />
              <button
                onClick={() => navigate("/instaladores")}
                className="text-sm font-medium text-slate-800 hover:text-slate-900 inline-flex items-center gap-1.5 hover:gap-2.5 transition-all pt-3 border-t border-slate-200"
              >
                {t("landing_v3.n3_cta")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Nota legal §00 */}
          <p className="text-xs text-slate-500 mt-8 text-center max-w-3xl mx-auto leading-relaxed">
            {t("landing_v3.s00_legal")}{" "}
            <a
              href="https://www.eusaswim.eu/documentation/standards-norms/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-slate-800 underline underline-offset-2"
            >
              {t("landing_v3.s00_legal_link")}
            </a>
            {" "}{t("landing_v3.s00_legal_after")}
          </p>
        </div>
      </section>

      {/* BLOQUE §02 - PISCINAS MUNICIPALES (Sprint 113.14 — B2G · vertical Contratación).
          Sprint 118.9 — Amagat via feature flag b2gVerticalVisible per no
          confondre el focus sostenibilitat. Codi al repo per reactivar. */}
      {isFeatureEnabled("b2gVerticalVisible") && (
      <section className="py-20 md:py-28 px-4 bg-slate-900 text-white" data-testid="municipal-section">
        <div className="max-w-5xl mx-auto">
          <div className="mb-14">
            <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-6">
              §02 · Piscinas municipales
            </p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-8 leading-tight" data-testid="municipal-title">
              Cada piscina municipal también es un<br className="hidden md:block" />
              <em className="italic font-normal text-blue-400">activo público</em>. Compárala con datos agregados.
            </h2>
            <div className="max-w-3xl space-y-5 text-lg text-slate-300 leading-relaxed">
              <p>
                Los municipios gestionan piscinas públicas que son patrimonio de la ciudadanía.
                <strong className="text-white"> Gestionarlas con inteligencia municipal agregada</strong> representa
                transparencia y eficiencia al servicio de lo que la piscina aporta a la
                ciudadanía: <strong className="text-white">integración social, salud y bienestar,
                progreso</strong>.
              </p>
              <p className="text-slate-400 text-base leading-relaxed">
                La Ficha Técnica municipal de PoolLex incorpora un dato público agregado único:
                el historial contractual de piscinas del sector en España — con más de
                <strong className="text-white"> 25.000 contratos analizados</strong> desde 2018.
              </p>
              <p className="text-xl md:text-2xl font-semibold text-white leading-snug border-l-2 border-blue-400 pl-5 mt-6">
                Aplica buenas prácticas de gestión a través de la
                <span className="text-blue-400"> Inteligencia Agregada Municipal</span>.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {/* Card 1 — Documentació técnica */}
            <div className="bg-white/5 border border-white/10 border-t-[3px] border-t-blue-400 p-7 flex flex-col gap-3 hover:bg-white/10 transition-colors" data-testid="municipal-doc">
              <p className="text-xs font-mono uppercase tracking-widest text-blue-400">
                01 · Documentación técnica
              </p>
              <h3 className="text-lg font-semibold text-white leading-tight">
                Firmada y trazable
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed flex-1">
                Cada piscina municipal, documentada técnicamente.
                Preparada para <strong className="text-white">renovaciones,
                ampliaciones y justificación pública</strong>.
              </p>
            </div>

            {/* Card 2 — Contexto contractual */}
            <div className="bg-white/5 border border-white/10 border-t-[3px] border-t-blue-400 p-7 flex flex-col gap-3 hover:bg-white/10 transition-colors" data-testid="municipal-contract">
              <p className="text-xs font-mono uppercase tracking-widest text-blue-400">
                02 · Contexto contractual
              </p>
              <h3 className="text-lg font-semibold text-white leading-tight">
                Historial del municipio
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed flex-1">
                Incorporamos el historial de contratación de piscinas de tu ayuntamiento:
                <strong className="text-white"> proveedores, importes, categorías</strong>.
                Un contexto útil para decisiones futuras.
              </p>
            </div>

            {/* Card 3 — Comparativa sectorial */}
            <div className="bg-white/5 border border-white/10 border-t-[3px] border-t-blue-400 p-7 flex flex-col gap-3 hover:bg-white/10 transition-colors" data-testid="municipal-benchmark">
              <p className="text-xs font-mono uppercase tracking-widest text-blue-400">
                03 · Benchmark sectorial
              </p>
              <h3 className="text-lg font-semibold text-white leading-tight">
                Comparativa municipal
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed flex-1">
                Compara tu piscina municipal con las de otros municipios similares —
                gasto, concurrencia, categorización. Detecta patrones anómalos con el
                <strong className="text-white"> motor de red flags</strong>.
              </p>
            </div>
          </div>

          {/* CTA únic al vertical Contratación */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-6 border-t border-white/10">
            <div className="flex-1">
              <p className="text-sm text-slate-400 leading-relaxed">
                Producto B2G · Servicio para ayuntamientos y gestores públicos
              </p>
            </div>
            <button
              onClick={() => navigate("/contratacion-publica")}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-none px-7 py-3.5 font-medium text-sm tracking-wide inline-flex items-center gap-2 shrink-0"
              data-testid="municipal-cta"
            >
              Explorar Contratación Pública
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
      )}

      {/* BLOQUE §03 - LA INFRAESTRUCTURA (moat visible amb 4 xifres).
          Sprint 118.9 — Amagat via feature flag b2gVerticalVisible: el
          moat "8.132 municipios · 46.000+ contratos · 25+ normas" reforça
          el pitch B2G/technic, no la certificació de sostenibilitat. */}
      {isFeatureEnabled("b2gVerticalVisible") && (
      <section className="py-20 md:py-28 px-4 bg-white" data-testid="moat-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-6">
              §03 · La infraestructura
            </p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6 leading-tight" data-testid="moat-title">
              Una plataforma sectorial que <em className="italic font-normal text-blue-700">ninguna otra</em> tiene.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
              PoolLex reúne tres capas que hasta ahora no existían juntas para el sector
              piscinas en España: la información municipal agregada, el criterio técnico
              y el marco normativo. Una infraestructura útil desde el primer proyecto.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-b border-slate-200">
            {/* Xifra 1 — Municipios */}
            <div className="p-6 md:p-8 border-b sm:border-b lg:border-b-0 lg:border-r border-slate-200 sm:[&:nth-child(2)]:border-r sm:[&:nth-child(3)]:border-r-0 lg:[&:nth-child(3)]:border-r" data-testid="moat-municipios">
              <div className="font-serif text-5xl md:text-6xl font-normal text-slate-900 leading-none mb-1 tabular-nums tracking-tight">
                8.132<span className="text-base font-mono text-blue-700 align-super ml-1">municipios</span>
              </div>
              <p className="font-medium text-slate-900 text-sm mt-4 mb-2">Municipios españoles cubiertos</p>
              <p className="text-sm text-slate-500 leading-snug mb-4">
                Base municipal completa a nivel estatal. Cada municipio con código INE,
                CCAA y provincia. Cobertura territorial única en el sector piscinas.
              </p>
              <p className="text-[0.7rem] font-mono uppercase tracking-widest text-slate-400 pt-3 border-t border-dashed border-slate-200">
                Fuente · INE 2026
              </p>
            </div>

            {/* Xifra 2 — Ayuntamientos */}
            <div className="p-6 md:p-8 border-b sm:border-b lg:border-b-0 lg:border-r border-slate-200" data-testid="moat-ayuntamientos">
              <div className="font-serif text-5xl md:text-6xl font-normal text-slate-900 leading-none mb-1 tabular-nums tracking-tight">
                12<span className="text-base font-mono text-blue-700 align-super ml-1">+</span>
              </div>
              <p className="font-medium text-slate-900 text-sm mt-4 mb-2">Ayuntamientos con normativa mapeada</p>
              <p className="text-sm text-slate-500 leading-snug mb-4">
                Sopela, Sant Fost, Begues, Cáceres, Palma, Álava, Badalona, Zuia,
                Markina… Cada uno con su ICM operativo, requisitos urbanísticos y forms pack.
              </p>
              <p className="text-[0.7rem] font-mono uppercase tracking-widest text-slate-400 pt-3 border-t border-dashed border-slate-200">
                Anchors reales · expedientes trabajados
              </p>
            </div>

            {/* Xifra 3 — Contratos */}
            <div className="p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-slate-200 sm:border-r" data-testid="moat-contratos">
              <div className="font-serif text-5xl md:text-6xl font-normal text-slate-900 leading-none mb-1 tabular-nums tracking-tight">
                {fmtInt(statsSnapshot.contratos)}
                <span className="text-base font-mono text-blue-700 align-super ml-1">contratos</span>
              </div>
              <p className="font-medium text-slate-900 text-sm mt-4 mb-2">Contratación pública piscinas analizada</p>
              {/* 11/10/2026 — la card deia 25.379 contratos i 136,8 M€ des del
                  14/07, i deia «toda la contratación menor» quan la dada ja
                  no és només menor: amb la font oficial hi entren també les
                  licitacions adjudicades, que són 1.408 dels 1.579 M€. Per
                  això ara s'anomenen els dos segments en comptes de dir
                  «toda».

                  Les xifres surten de la instantània que genera el seed
                  (src/data/contratacion_stats.json), NO del hook que crida
                  l'API. És deliberat: aquest bloc està amagat rere
                  b2gVerticalVisible i la landing és la pàgina més visitada
                  del lloc — no té sentit que cada visita faci una petició
                  per a un bloc que no es pinta. La instantània es refà a
                  cada refresc de la dada, així que tampoc no la manté
                  ningú. Si algun dia s'activa el flag i es vol en viu,
                  useContratacionStats() és una línia.

                  I ara diu el MATEIX número que /contratacion-publica.
                  Abans n'hi havia dos (46.386 aquí, 46.473 allà) perquè
                  cadascun comptava una finestra d'anys diferent, i la
                  diferència no l'hauria sabuda explicar ningú. */}
              <p className="text-sm text-slate-500 leading-snug mb-4">
                Contratación menor y licitaciones adjudicadas del sector piscinas en España{" "}
                {statsSnapshot.anio_min}–{statsSnapshot.anio_max}:
                <strong className="text-slate-900"> {fmtMEur(statsSnapshot.importe_total_eur)}</strong>{" "}
                trazados, más de {fmtMilesAbajoRaw(statsSnapshot.empresas)} empresas
                clasificadas. Motor red flags antifraude propio.
              </p>
              <p className="text-[0.7rem] font-mono uppercase tracking-widest text-slate-400 pt-3 border-t border-dashed border-slate-200">
                Plataforma Contratación Sector Público
              </p>
            </div>

            {/* Xifra 4 — Normativas. Sprint 116 — detall plegable via
                <details> perquè les 4 cards quedin visualment uniformes a
                simple vista. El resum encaixa amb la densitat de text de
                les cards 1-3; el marc complet s'obre a demanda. */}
            <div className="p-6 md:p-8" data-testid="moat-normativas">
              <div className="font-serif text-5xl md:text-6xl font-normal text-slate-900 leading-none mb-1 tabular-nums tracking-tight">
                25<span className="text-base font-mono text-blue-700 align-super ml-1">+ normas</span>
              </div>
              <p className="font-medium text-slate-900 text-sm mt-4 mb-2">Marco técnico integrado</p>
              <p className="text-sm text-slate-500 leading-snug mb-4">
                Edificación y CTE, procedimiento administrativo, catastral, sanitario, ambiental
                y autonómico (Cataluña, País Vasco, Extremadura…) + urbanismo municipal.
              </p>
              <details className="group text-sm text-slate-500 leading-snug mb-4 [&_summary]:list-none">
                <summary className="cursor-pointer text-blue-700 hover:text-blue-900 font-medium text-xs inline-flex items-center gap-1 select-none">
                  <span className="group-open:hidden">Ver marco completo</span>
                  <span className="hidden group-open:inline">Ocultar detalle</span>
                  <svg className="w-3 h-3 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </summary>
                <div className="mt-3 space-y-2">
                  <p><strong className="text-slate-700">Edificación y CTE:</strong> LOE (Ley 38/1999), RD 314/2006 (CTE), CTE DB-HS4 agua, DB-SE-AE acciones, DB-SI incendios, EHE-08 hormigón, REBT RD 842/2002 + ITC-BT-31 piscinas.</p>
                  <p><strong className="text-slate-700">Procedimiento y catastro:</strong> Ley 39/2015 PAC, Ley 40/2015 RJSP, RD Leg 7/2015 Ley del Suelo, Ley 16/2012 catastral, LPH Ley 49/1960.</p>
                  <p><strong className="text-slate-700">Sanitario y ambiental:</strong> RD 742/2013 sanitario piscinas, RD 105/2008 residuos, RD 1627/1997 seguridad y salud.</p>
                  <p><strong className="text-slate-700">Autonómico:</strong> Decret 89/2010 (PROGROC, Cataluña), Decret 64/2014 (RPLUC), Decret 179/1995 (ROAS), Llei 20/2009 ambiental, Decret 112/2012 (País Vasco), LSOTEX Ley 11/2018 + Ley 2/1999 + Decret 23/2018 patrimonio (Extremadura), reglamentos autonómicos de piscinas de uso público.</p>
                  <p><strong className="text-slate-700">Local:</strong> POUM / PGOU / NNSS + ordenanzas municipales y fiscales (ICIO, tasa licencia) aplicables por municipio.</p>
                </div>
              </details>
              <p className="text-[0.7rem] font-mono uppercase tracking-widest text-slate-400 pt-3 border-t border-dashed border-slate-200">
                Criterio técnico colegiado
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* BLOQUE §04 - HERRAMIENTAS COMPLEMENTARIAS. Sprint 116 —
          reformulat per no trencar el fil narratiu que ve del §02
          (municipal) i §03 (infraestructura sectorial). Ja no es
          presenta com a alternativa "abans de la Ficha", sinó com a
          conjunt d'eines públiques del mateix ecosistema. */}
      <section className="py-16 md:py-20 px-4 bg-slate-50" data-testid="access-section">
        <div className="max-w-4xl mx-auto">
          {/* Sprint 119.93 — Fix: text estava hardcoded en ES ignorant el
              i18n. Ara consumeix landing_v3.s04_* (corregit Sprint 119.92
              per treure "gestor municipal" fora d'scope EN 17645). */}
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4 text-center">
            {t('landing_v3.s04_label', '§04 · Herramientas complementarias')}
          </p>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-3 text-center">
            {t('landing_v3.s04_title', 'Utilidades técnicas abiertas, sin registro.')}
          </h2>
          <p className="text-slate-600 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('landing_v3.s04_intro', 'Piezas del mismo ecosistema que puedes usar directamente — tanto si eres particular como profesional del sector.')}
          </p>

          {/* Sprint 118.8 — La card EN 17645 s'ha promogut al bloc §00
              (nivells PoolLex). Aquí queden nomes les 2 utilitats
              transversals gratuites. */}
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <div
              className="bg-white border border-slate-200 p-7 hover:border-blue-700 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate("/pool-check")}
              data-testid="access-check"
            >
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-blue-700" />
                <p className="text-xs font-mono uppercase tracking-widest text-blue-700">{t("landing_v3.s04_check_label")}</p>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t("landing_v3.s04_check_title")}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">{t("landing_v3.s04_check_body")}</p>
              <span className="text-sm font-medium text-blue-700 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                {t("landing_v3.s04_check_cta")}
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>

            {/* NOU · Card Sostenibilidad EN 17645 */}
            <div
              className="bg-white border border-emerald-200 p-7 hover:border-emerald-600 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate("/ficha-tecnica")}
              data-testid="access-sustainability"
            >
              <div className="flex items-center gap-3 mb-4">
                <Leaf className="w-5 h-5 text-emerald-600" />
                <p className="text-xs font-mono uppercase tracking-widest text-emerald-700">{t("landing_v3.s04_sust_label")}</p>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t("landing_v3.s04_sust_title")}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">{t("landing_v3.s04_sust_body")}</p>
              <span className="text-sm font-medium text-emerald-700 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                {t("landing_v3.s04_sust_cta")}
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>

            <div
              className="bg-white border border-slate-200 p-7 hover:border-blue-700 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate("/calculadoras")}
              data-testid="access-calculators"
            >
              <div className="flex items-center gap-3 mb-4">
                <Calculator className="w-5 h-5 text-blue-700" />
                <p className="text-xs font-mono uppercase tracking-widest text-blue-700">{t("landing_v3.s04_calc_label")}</p>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t("landing_v3.s04_calc_title")}</h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">{t("landing_v3.s04_calc_body")}</p>
              <span className="text-sm font-medium text-blue-700 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                {t("landing_v3.s04_calc_cta")}
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Sprint 119.61 — MINI-BLOC "Actividad reciente" mogut sota el mapa
          (Sprint 118.4 original). Aquí quedava aïllat abans del CTA final;
          integrat amb el widget "Busca tu piscina" té més impacte. */}

      {/* BLOQUE 5 - CTA FINAL */}
      <section className="py-24 md:py-32 px-4 bg-white" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mb-6" data-testid="final-cta-title">
            {t("home.final_title")}
          </h2>
          <p className="text-lg text-slate-500 mb-10 leading-relaxed">
            {t("home.final_subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/ficha-tecnica")}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-none px-10 py-4 font-semibold transition-all text-sm tracking-wide inline-flex items-center justify-center gap-2"
              data-testid="final-cta-passport"
            >
              {t("home.cta_passport")}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/initiative")}
              className="border border-slate-300 text-slate-700 hover:border-slate-500 hover:text-slate-900 rounded-none px-10 py-4 font-semibold transition-all text-sm tracking-wide inline-flex items-center justify-center gap-2"
              data-testid="final-cta-initiative"
            >
              {t("home.cta_initiative")}
            </button>
          </div>
          <p className="text-slate-400 text-xs mt-6 font-mono">{t("generator.noAccount")}</p>
          {/* Sprint 165 — La prova social numèrica ja no és aquí: ha pujat
              just sota del hero, abans del mapa. Al peu només la veia qui
              arribava al final de la pàgina. */}
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
};

export default LandingPageV2;
